import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
  Overlay,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  linkedSignal,
  NgZone,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatPseudoCheckbox } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

type ChipMultiSelectValue = string | number;

interface SelectedChip {
  readonly value: ChipMultiSelectValue;
  readonly label: string;
}

interface NormalizedOption {
  readonly id: string;
  readonly value: ChipMultiSelectValue;
  readonly label: string;
}
let nextUniqueId = 0;

const CLOSED_OPEN_KEYS: ReadonlySet<string> = new Set([
  'Enter',
  ' ', //space
  'ArrowDown',
  'ArrowUp',
]);

const isSelectableValue = (value: unknown): value is ChipMultiSelectValue =>
  typeof value === 'string' || typeof value === 'number';

const readProperty = (source: object, key: string): unknown => {
  const value: unknown = Reflect.get(source, key);
  return value;
};

@Component({
  selector: 'lib-chip-multi-select',
  standalone: true,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatPseudoCheckbox,
  ],
  templateUrl: './chip-multi-select.component.html',
  styleUrl: './chip-multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipMultiSelectComponent),
      multi: true,
    },
  ],
})
export class ChipMultiSelectComponent
  implements ControlValueAccessor, AfterViewInit, OnDestroy
{
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);

  readonly options = input.required<readonly object[]>();
  readonly loading = input(false);
  readonly valueKey = input('value');
  readonly labelKey = input('label');
  readonly comboboxLabel = input<string | null>(null);
  readonly comboboxLabelledBy = input<string | null>(null);

  readonly openedChange = output<boolean>();
  readonly openRequested = output<void>();

  readonly selectedValues = signal<ChipMultiSelectValue[]>([]);
  readonly disabled = signal(false);
  readonly isPanelOpen = signal(false);

  readonly overlayOrigin = viewChild.required(CdkOverlayOrigin);
  readonly connectedOverlay = viewChild(CdkConnectedOverlay);
  private readonly trigger =
    viewChild.required<ElementRef<HTMLElement>>('trigger');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  private readonly uniqueId = nextUniqueId++;
  readonly listboxId = `lib-chip-multi-select-${this.uniqueId}-listbox`;

  /** Below first, above as fallback; both start-aligned (RTL-aware). */
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];

  /** Scroll closing is handled by the component's scoped document listener. */
  readonly scrollStrategy: ScrollStrategy =
    inject(Overlay).scrollStrategies.noop();

  /** Valid, de-duplicated (first wins) options in their original order. */
  readonly normalizedOptions = computed<readonly NormalizedOption[]>(() => {
    const valueKey = this.valueKey();
    const labelKey = this.labelKey();
    const seen = new Set<ChipMultiSelectValue>();
    const result: NormalizedOption[] = [];

    for (const source of this.options()) {
      const value = readProperty(source, valueKey);
      if (!isSelectableValue(value) || seen.has(value)) {
        continue;
      }
      seen.add(value);
      const label = readProperty(source, labelKey);
      result.push({
        id: `${this.listboxId}-option-${result.length}`,
        value,
        label: isSelectableValue(label) ? String(label) : '',
      });
    }
    return result;
  });

  private readonly optionByValue = computed(
    () =>
      new Map<ChipMultiSelectValue, NormalizedOption>(
        this.normalizedOptions().map(
          (option): [ChipMultiSelectValue, NormalizedOption] => [
            option.value,
            option,
          ]
        )
      )
  );

  readonly selectedSet = computed(() => new Set(this.selectedValues()));

  readonly selectedChips = computed<readonly SelectedChip[]>(() => {
    const optionMap = this.optionByValue();
    return this.selectedValues().map((value) => ({
      value,
      label: optionMap.get(value)?.label ?? String(value),
    }));
  });

  /** Resets to -1 whenever the option list changes. */
  readonly activeIndex = linkedSignal<readonly NormalizedOption[], number>({
    source: this.normalizedOptions,
    computation: () => -1,
  });

  private onChange: (value: ChipMultiSelectValue[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private resizeObserver?: ResizeObserver;

  constructor() {
    this.registerCloseOnScrollEffect();
  }

  private registerCloseOnScrollEffect(): void {
    effect((onCleanup) => {
      if (!this.isPanelOpen()) {
        return;
      }

      const listener = (event: Event): void => {
        this.onScroll(event);
      };

      const scrollOptions: AddEventListenerOptions = {
        capture: true,
        passive: true,
      };

      this.ngZone.runOutsideAngular(() => {
        this.document.addEventListener('scroll', listener, scrollOptions);
      });

      onCleanup(() => {
        this.document.removeEventListener('scroll', listener, scrollOptions);
      });
    });
  }

  private onScroll(event: Event): void {
    if (!this.isPanelOpen()) {
      return;
    }

    const target = event.target;
    const panel = this.panel()?.nativeElement;

    if (
      target instanceof Node &&
      (panel === target || panel?.contains(target))
    ) {
      return;
    }

    this.ngZone.run(() => {
      this.closeDropdown();
    });
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.syncOverlayGeometry();
    });
    this.resizeObserver.observe(this.originElement());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  // ---- ControlValueAccessor ----

  writeValue(value: unknown): void {
    this.selectedValues.set(
      Array.isArray(value) ? value.filter(isSelectableValue) : []
    );
  }

  registerOnChange(fn: (value: ChipMultiSelectValue[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled && this.isPanelOpen()) {
      this.closeDropdown();
    }
  }

  // ---- Selection ----

  updateSelection(values: readonly unknown[]): void {
    const previousValues = this.selectedValues();
    const incomingValues = values.filter(isSelectableValue);
    const incomingSet = new Set(incomingValues);
    const previousSet = new Set(previousValues);
    const orderedValues = [
      ...previousValues.filter((value) => incomingSet.has(value)),
      ...incomingValues.filter((value) => !previousSet.has(value)),
    ];
    this.selectedValues.set(orderedValues);
    this.onChange(orderedValues);
  }

  toggleOption(option: NormalizedOption): void {
    if (this.disabled() || this.loading()) {
      return;
    }
    const current = this.selectedValues();
    this.updateSelection(
      this.selectedSet().has(option.value)
        ? current.filter((value) => value !== option.value)
        : [...current, option.value]
    );
  }

  removeOption(value: ChipMultiSelectValue, event: Event): void {
    event.stopPropagation();
    this.updateSelection(
      this.selectedValues().filter((selected) => selected !== value)
    );
    this.onTouched();
    this.focusTrigger();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.updateSelection([]);
    this.onTouched();
    this.focusTrigger();
  }

  // ---- Open / close ----

  openDropdown(): void {
    if (this.disabled() || this.isPanelOpen()) {
      return;
    }
    // The parent may set loading=true synchronously; the overlay renders on the
    // next change detection pass with the updated input, so no deferral is needed.
    this.openRequested.emit();
    this.isPanelOpen.set(true);
    this.openedChange.emit(true);
  }

  closeDropdown(restoreFocus = false): void {
    if (!this.isPanelOpen()) {
      return;
    }
    this.isPanelOpen.set(false);
    this.openedChange.emit(false);
    this.onTouched();
    if (restoreFocus) {
      this.focusTrigger();
    }
  }

  toggleDropdown(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isPanelOpen()) {
      this.closeDropdown();
      return;
    }
    this.openDropdown();
    this.focusTrigger();
  }

  onTriggerClick(): void {
    this.openDropdown();
  }

  onOutsideClick(event: MouseEvent): void {
    const target = event.target;
    if (target instanceof Node && this.originElement().contains(target)) {
      return;
    }
    this.closeDropdown();
  }

  // ---- Keyboard ----

  onTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }
    if (this.isPanelOpen() && (event.key === 'Escape' || event.key === 'Tab')) {
      this.handleDismissKey(event);
      return;
    }
    // Ignore keys bubbling from chip-remove / clear buttons.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (this.isPanelOpen()) {
      this.handleOpenKey(event);
    } else {
      this.handleClosedKey(event);
    }
  }

  private handleDismissKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown(true);
      return;
    }
    this.closeDropdown(); // Tab: close without trapping focus.
  }

  private handleClosedKey(event: KeyboardEvent): void {
    if (!CLOSED_OPEN_KEYS.has(event.key)) {
      return;
    }
    event.preventDefault();
    this.openDropdown();
    this.setActive(this.initialActiveIndex());
  }

  private handleOpenKey(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        this.moveActive(1);
        break;
      case 'ArrowUp':
        this.moveActive(-1);
        break;
      case 'Home':
        this.setActive(0);
        break;
      case 'End':
        this.setActive(this.normalizedOptions().length - 1);
        break;
      case 'Enter':
      case ' ':
        this.toggleActiveOption();
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  private moveActive(delta: 1 | -1): void {
    const last = this.normalizedOptions().length - 1;
    const current = this.activeIndex();
    if (current < 0) {
      this.setActive(delta > 0 ? 0 : last);
      return;
    }
    this.setActive(Math.min(Math.max(current + delta, 0), last));
  }

  private setActive(index: number): void {
    if (
      this.loading() ||
      index < 0 ||
      index >= this.normalizedOptions().length
    ) {
      return;
    }
    this.activeIndex.set(index);
    this.scrollActiveIntoView();
  }

  private initialActiveIndex(): number {
    const options = this.normalizedOptions();
    const selected = this.selectedSet();

    const selectedIndex = options.findIndex((option) =>
      selected.has(option.value)
    );

    return Math.max(selectedIndex, 0);
  }

  onOptionKeydown(option: NormalizedOption, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.toggleOption(option);
  }

  private toggleActiveOption(): void {
    const index = this.activeIndex();
    const options = this.normalizedOptions();
    if (index >= 0 && index < options.length) {
      this.toggleOption(options[index]);
    }
  }

  private scrollActiveIntoView(): void {
    afterNextRender(
      () => {
        const option = this.normalizedOptions()[this.activeIndex()];
        const panel = this.panel()?.nativeElement;

        if (option && panel) {
          panel.ownerDocument
            .getElementById(option.id)
            ?.scrollIntoView({ block: 'nearest' });
        }
      },
      { injector: this.injector }
    );
  }

  // ---- Overlay geometry ----

  syncOverlayGeometry(): void {
    const overlayRef = this.connectedOverlay()?.overlayRef;
    if (!this.isPanelOpen() || !overlayRef) {
      return;
    }
    overlayRef.updateSize({
      width: this.originElement().getBoundingClientRect().width,
    });
    overlayRef.updatePosition();
  }

  private originElement(): HTMLElement {
    return this.overlayOrigin().elementRef.nativeElement;
  }

  private focusTrigger(): void {
    this.trigger().nativeElement.focus();
  }
}
